import { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Logo from './Logo'
import LienAccueil from './components/LienAccueil'
import { useAuth } from './AuthContext'
import { EMAIL_CONTACT, NOM_PRODUIT } from './constants'
// Cohérence visuelle globale (retour Marc 12/07) : les pages « coquille » (/collectivites,
// /about, /faq, mentions, CGV…) adoptent l'identité de l'observatoire — titres IBM Plex Serif.
// La classe `.landing` (index.css) porte déjà la règle serif ; on la réutilise ici.
import '@fontsource/ibm-plex-serif/400.css'
import '@fontsource/ibm-plex-serif/600.css'
import '@fontsource/ibm-plex-sans/400.css'
import '@fontsource/ibm-plex-sans/500.css'
import '@fontsource/ibm-plex-sans/600.css'
import './obs/obs.css'

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, logout } = useAuth()
  const location = useLocation()
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const logoutRef = useRef<HTMLDivElement>(null)

  // Close on click outside
  useEffect(() => {
    if (!showLogoutConfirm) return
    function handleClick(e: MouseEvent) {
      if (logoutRef.current && !logoutRef.current.contains(e.target as Node)) {
        setShowLogoutConfirm(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showLogoutConfirm])

  // Catalogue retiré de la nav (abonnement exclusif — décision Marc 04/07, CDC-01) ;
  // les routes/pages légales restent servies pour les liens existants.
  const navItems = [
    { path: '/', label: 'Accueil' },
    { path: '/#recherche', label: 'Observatoire' },
  ]

  // « Mon espace » (/espace) : RETIRÉ de la navigation le 02/08 (Marc : « il est encore
  // calqué sur l'ancien modèle de vente » — la page lisait /api/orders et /api/reports, le
  // catalogue d'études fermé le 11/07). ROUVERT le 07/09 (lot C) : /espace est désormais
  // l'espace client user-scopé (/api/me/* — abonnements, devis et commandes, factures,
  // compte). L'historique du catalogue vit sous /admin/archives.
  if (user?.role === 'analyst') {
    // Un consultant (ABS, 14/10) n'a que ses études : ni observatoire, ni espace client.
    navItems.splice(0, navItems.length, { path: '/etudes', label: 'Mes études' })
  } else if (user) {
    navItems.push({ path: '/espace', label: 'Mon espace' })
  }
  if (isAdmin) {
    navItems.push({ path: '/admin', label: 'Dashboard Direction' })
  }

  const currentYear = new Date().getFullYear()

  return (
    <div className="obs2-jetons landing min-h-screen bg-(--c-bg) text-(--c-text) flex flex-col">
      {/* A11y : skip-to-content pour utilisateurs clavier (WCAG 2.4.1) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-marque-marine focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg"
      >
        Aller au contenu principal
      </a>
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap justify-between items-center gap-y-2 sticky top-0 z-50" role="banner">
        <LienAccueil className="flex items-center" aria-label={`Accueil ${NOM_PRODUIT}`}>
          <Logo />
        </LienAccueil>
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <nav className="flex flex-wrap gap-x-4 gap-y-1" aria-label="Navigation principale">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`inline-flex items-center text-sm py-2.5 ${
                  // Comparaison par PRÉFIXE : le dashboard vit désormais sous /admin/ventes
                  // et /admin/crm (02/08). Une égalité stricte n'aurait plus jamais
                  // surligné « Dashboard Direction ».
                  location.pathname === item.path
                  || location.pathname.startsWith(item.path + '/')
                    ? 'font-medium text-marque-marine border-b-2 border-marque-marine'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          {user ? (
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-200">
              <span className="text-sm text-(--c-muted)">{user.name}</span>
              <div className="relative" ref={logoutRef}>
                <button
                  onClick={() => setShowLogoutConfirm(prev => !prev)}
                  className="text-xs text-red-500 hover:text-red-700"
                  aria-haspopup="dialog"
                  aria-expanded={showLogoutConfirm}
                >
                  Déconnexion
                </button>
                {showLogoutConfirm && (
                  <div
                    className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50"
                    role="dialog"
                    aria-labelledby="logout-dialog-title"
                  >
                    <p id="logout-dialog-title" className="text-sm text-gray-700 mb-3">
                      Êtes-vous sûr de vouloir vous déconnecter ?
                    </p>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setShowLogoutConfirm(false)}
                        className="text-xs px-3 py-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={() => {
                          setShowLogoutConfirm(false)
                          logout()
                        }}
                        className="text-xs px-3 py-1.5 rounded-md bg-red-500 text-white hover:bg-red-600 transition"
                      >
                        Se déconnecter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Charte de l'observatoire (15/10) : bouton marine de `.obs2-btn-primary`, plus le bleu vif.
            <Link to="/login" className="text-sm font-semibold text-white bg-marque-marine hover:opacity-90 rounded-lg px-3 py-1.5 ml-2 sm:ml-4 transition">
              Connexion
            </Link>
          )}
        </div>
      </header>
      <main id="main-content" className="flex-1" tabIndex={-1}>{children}</main>

      {/* Footer global — UX-36 / LEGAL-1 : liens légaux accessibles partout */}
      <footer className="bg-white border-t border-(--c-border) mt-12">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-sm">
            <div>
              <p className="font-semibold text-(--c-brand) mb-3">{NOM_PRODUIT}</p>
              <p className="text-(--c-muted) leading-relaxed">
                Les données officielles de chaque commune, réunies, sourcées et
                expliquées — actualisées tous les mois.
              </p>
            </div>
            <div>
              <p className="font-semibold text-(--c-brand) mb-3">Produit</p>
              <ul className="space-y-2 text-(--c-muted)">
                <li><Link to="/#recherche" className="inline-block py-2 hover:text-(--c-brand) transition">L'Observatoire</Link></li>
                <li><Link to="/#tarifs" className="inline-block py-2 hover:text-(--c-brand) transition">Tarifs</Link></li>
                <li><Link to="/collectivites" className="inline-block py-2 hover:text-(--c-brand) transition">Collectivités : comment s'abonner</Link></li>
                <li><Link to="/faq" className="inline-block py-2 hover:text-(--c-brand) transition">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-(--c-brand) mb-3">Entreprise</p>
              <ul className="space-y-2 text-(--c-muted)">
                <li><Link to="/about" className="inline-block py-2 hover:text-(--c-brand) transition">À propos</Link></li>
                <li><Link to="/contact" className="inline-block py-2 hover:text-(--c-brand) transition">Contact</Link></li>
                <li>
                  <a href={`mailto:${EMAIL_CONTACT}`} className="inline-block py-2 hover:text-(--c-brand) transition">
                    {EMAIL_CONTACT}
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-(--c-brand) mb-3">Légal</p>
              <ul className="space-y-2 text-(--c-muted)">
                <li><Link to="/mentions-legales" className="inline-block py-2 hover:text-(--c-brand) transition">Mentions légales</Link></li>
                <li><Link to="/cgv" className="inline-block py-2 hover:text-(--c-brand) transition">CGU / CGV</Link></li>
                <li><Link to="/confidentialite" className="inline-block py-2 hover:text-(--c-brand) transition">Confidentialité</Link></li>
                <li><Link to="/accessibilite" className="inline-block py-2 hover:text-(--c-brand) transition">Accessibilité</Link></li>
                <li><a href="/licences-tiers.txt" className="inline-block py-2 hover:text-(--c-brand) transition">Licences des composants tiers</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-(--c-border) mt-8 pt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-(--c-muted)">
            <p>© {currentYear} {NOM_PRODUIT} — Tous droits réservés.</p>
            <p>Conditions complètes dans <Link to="/mentions-legales" className="underline hover:text-(--c-brand)">les mentions légales</Link>.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
